import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { trpc } from '~/api/trpc';

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
    }).format(amount);
};

type RefundPaymentDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    billingDraftId: string;
    storeId: number;
    payments: Array<{ status: string; paidAmountCents: number }> | undefined;
    totalGrossDiscounted: number;
    onSuccess?: () => void;
};

const RefundPaymentDialog = ({
    open,
    onOpenChange,
    billingDraftId,
    storeId,
    payments,
    totalGrossDiscounted,
    onSuccess,
}: RefundPaymentDialogProps) => {
    const [refundAmount, setRefundAmount] = useState('');
    const [refundMethod, setRefundMethod] = useState<'CASH' | 'GHL_MANUAL_PAYMENT' | ''>('');
    const [refundNote, setRefundNote] = useState('');
    const [refundCashRegisterId, setRefundCashRegisterId] = useState('');

    const { data: cashRegistersData } = trpc.cashRegisters.getByStore.useQuery(
        { storeId },
        { enabled: !!storeId && refundMethod === 'CASH' && open }
    );

    useEffect(() => {
        if (cashRegistersData && !refundCashRegisterId) {
            const defaultId = cashRegistersData.defaultCashRegisterId;
            const isDefaultActive = defaultId && cashRegistersData.cashRegisters.some((cr) => cr.id === defaultId);
            if (isDefaultActive) {
                setRefundCashRegisterId(defaultId);
            }
        }
    }, [cashRegistersData, refundCashRegisterId]);

    const addRefundPaymentMutation = trpc.payments.addRefundPaymentToBillingDraft.useMutation({
        onSuccess: () => {
            toast.success('Rückzahlung wurde erfolgreich hinzugefügt');
            resetState();
            onOpenChange(false);
            onSuccess?.();
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const resetState = () => {
        setRefundAmount('');
        setRefundMethod('');
        setRefundNote('');
        setRefundCashRegisterId('');
    };

    const netPaidCents = payments?.reduce((sum, p) =>
        p.status === 'COMPLETED' ? sum + p.paidAmountCents : sum, 0
    ) ?? 0;
    const totalPaidEur = netPaidCents / 100;
    const maxRefundEur = Math.max(0, totalPaidEur - totalGrossDiscounted);
    const enteredRefund = parseFloat(refundAmount) || 0;
    const exceedsMax = enteredRefund > maxRefundEur + 0.001;

    return (
        <AlertDialog
            open={open}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    resetState();
                }
                onOpenChange(isOpen);
            }}
        >
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Rückzahlung hinzufügen</AlertDialogTitle>
                    <AlertDialogDescription>
                        Erfassen Sie eine Rückzahlung an den Kunden. Der Betrag wird von der Gesamtzahlung abgezogen.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <div className='space-y-4'>
                    <div className='space-y-2'>
                        <Label htmlFor='refund-method'>Zahlungsmethode</Label>
                        <Select value={refundMethod} onValueChange={(v) => setRefundMethod(v as any)}>
                            <SelectTrigger id='refund-method'>
                                <SelectValue placeholder='Methode wählen' />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value='CASH'>Bar</SelectItem>
                                <SelectItem value='GHL_MANUAL_PAYMENT'>Sonstige</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {refundMethod === 'CASH' && (
                        <div className='space-y-2'>
                            <Label>Kasse</Label>
                            <Select value={refundCashRegisterId} onValueChange={setRefundCashRegisterId} disabled>
                                <SelectTrigger className='opacity-70'>
                                    <SelectValue placeholder='Kasse auswählen' />
                                </SelectTrigger>
                                <SelectContent>
                                    {cashRegistersData?.cashRegisters?.map((cr) => (
                                        <SelectItem key={cr.id} value={cr.id}>
                                            {cr.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {cashRegistersData && cashRegistersData.cashRegisters.length === 0 && (
                                <p className='text-xs text-destructive'>
                                    Keine Kassen für diesen Standort vorhanden.
                                </p>
                            )}
                        </div>
                    )}
                    <div className='space-y-2'>
                        <Label htmlFor='refund-amount'>Betrag (€)</Label>
                        <Input
                            id='refund-amount'
                            type='number'
                            min='0.01'
                            step='0.01'
                            max={maxRefundEur}
                            placeholder='0,00'
                            value={refundAmount}
                            onChange={(e) => setRefundAmount(e.target.value)}
                        />
                        <p className='text-xs text-muted-foreground'>
                            Maximal erstattbar: {formatCurrency(maxRefundEur)}
                        </p>
                        {exceedsMax && (
                            <p className='text-xs text-destructive'>
                                Betrag darf nicht höher als {formatCurrency(maxRefundEur)} sein
                            </p>
                        )}
                    </div>
                    <div className='space-y-2'>
                        <Label htmlFor='refund-note'>Notiz (optional)</Label>
                        <Input
                            id='refund-note'
                            placeholder='Grund der Rückzahlung...'
                            value={refundNote}
                            onChange={(e) => setRefundNote(e.target.value)}
                        />
                    </div>
                </div>
                <AlertDialogFooter>
                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                    <AlertDialogAction
                        disabled={!refundMethod || !refundAmount || enteredRefund <= 0 || exceedsMax || addRefundPaymentMutation.isPending || (refundMethod === 'CASH' && !refundCashRegisterId)}
                        onClick={() => {
                            if (refundMethod && refundAmount) {
                                addRefundPaymentMutation.mutate({
                                    billingDraftId,
                                    paidAmountCents: Math.round(enteredRefund * 100),
                                    paymentMethod: refundMethod,
                                    note: refundNote || undefined,
                                    ...(refundMethod === 'CASH' && refundCashRegisterId ? { cashRegisterId: refundCashRegisterId } : {}),
                                });
                            }
                        }}
                        className='bg-destructive text-white hover:bg-destructive/90'
                    >
                        {addRefundPaymentMutation.isPending ? (
                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        ) : null}
                        Rückzahlung erfassen
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

export default RefundPaymentDialog;
